/**
 * ARTICLE21 Google Analytics 4 추적 시스템
 * 아트 플랫폼에 특화된 사용자 행동 분석 유틸리티
 */

import { parseUserFromStorage } from '@/utils/userStorage';

// 이벤트 타입 정의
export const EVENT_TYPES = {
  // 페이지 네비게이션
  PAGE_VIEW: 'page_view',
  
  // 사용자 인증
  LOGIN: 'login',
  SIGNUP_START: 'signup_start',
  SIGNUP_COMPLETE: 'signup_complete',
  LOGOUT: 'logout',
  
  // 콘텐츠 조회
  ARTWORK_VIEW: 'artwork_view',
  ARTIST_VIEW: 'artist_view',
  ARTICLE_VIEW: 'article_view',
  PORTFOLIO_VIEW: 'portfolio_view',
  EXHIBITION_VIEW: 'exhibition_view',
  NEWS_VIEW: 'news_view',
  SPECIAL_VIEW: 'special_view',
  
  // 상호작용
  ARTWORK_LIKE: 'artwork_like',
  ARTIST_LIKE: 'artist_like',
  ARTICLE_LIKE: 'article_like',
  SHARE: 'share',
  DOWNLOAD: 'download',
  
  // 검색 및 탐색
  SEARCH: 'search',
  FILTER_APPLY: 'filter_apply',
  CATEGORY_BROWSE: 'category_browse',
  
  // 전환 액션
  INQUIRY_SUBMIT: 'inquiry_submit',
  CONTEST_APPLY: 'contest_apply',
  COLLABORATION_APPLY: 'collaboration_apply',
  CONTACT_CLICK: 'contact_click',
  PROFILE_UPDATE: 'profile_update',
  
  // 사용자 참여
  SCROLL_DEPTH: 'scroll_depth',
  TIME_ON_PAGE: 'time_on_page',
  SESSION_START: 'session_start'
}

// 콘텐츠 타입 정의
export const CONTENT_TYPES = {
  ARTWORK: 'artwork',
  ARTIST: 'artist', 
  ARTICLE: 'article',
  EXHIBITION: 'exhibition',
  NEWS: 'news',
  SPECIAL: 'special',
  PORTFOLIO: 'portfolio',
  HOME: 'home',
  SEARCH: 'search',
  PROFILE: 'profile'
}

// 사용자 타입 정의
export const USER_TYPES = {
  GUEST: 'guest',
  MEMBER: 'member',
  ARTIST: 'artist',
  ADMIN: 'admin'
}

export function useAnalytics() {
  const GA_ID = 'G-9H3LQC0M36'
  
  // 개발 환경 체크 유틸리티
  const isDev = () => process.env.NODE_ENV === 'development'

  // 운영 도메인에서만 GA 전송 (비운영 dev/stg/local 트래픽이 운영 GA 속성 오염 방지)
  const isAnalyticsEnabled = () =>
    typeof window !== 'undefined' &&
    ['mimio.life', 'www.mimio.life'].includes(window.location.hostname)

  /**
   * 안전한 gtag 호출 래퍼 (향상된 에러 처리)
   */
  const safeGtag = (...args) => {
    // 비운영 환경이면 전송하지 않음
    if (!isAnalyticsEnabled()) {
      return false;
    }

    // 환경 검증
    if (typeof window === 'undefined' || !window.gtag) {
      return false;
    }
    
    // gtag 함수 유효성 검증
    if (typeof window.gtag !== 'function') {
      if (isDev()) {
        console.warn('Analytics: gtag is not a function');
      }
      return false;
    }
    
    try {
      window.gtag(...args);
      return true;
    } catch (error) {
      // 프로덕션에서는 조용히 실패, 개발환경에서만 로그
      if (isDev()) {
        console.warn('Analytics tracking error:', error);
      }
      return false;
    }
  }
  
  /**
   * 현재 사용자 타입 가져오기 (localStorage 기반) - 개선된 디버깅 포함
   */
  const getCurrentUserType = () => {
    try {
      const user = parseUserFromStorage();
      
      // user가 null인 경우 조기 반환
      if (!user) {
        return USER_TYPES.GUEST;
      }
      
      // 디버깅용 로깅 (개발 환경에서만)
      if (isDev() && window?.location?.search?.includes('debug')) {
        console.log('🔍 getCurrentUserType debug:', {
          user,
          hasId: !!user.id,
          userRoleType: user.userRoleType,
          roleType: user.roleType
        });
      }
      
      // id 필드 또는 accessToken 존재 시 로그인 상태로 판단
      if (user.id || user.accessToken) {
        // 여러 가능한 필드명 체크 (userType 제거 - parseUserFromStorage에서 반환하지 않음)
        const roleType = user.userRoleType || user.roleType;
        return roleType === 'ARTIST' ? USER_TYPES.ARTIST : USER_TYPES.MEMBER;
      }
      return USER_TYPES.GUEST;
    } catch (error) {
      if (isDev()) {
        console.warn('getCurrentUserType error:', error);
      }
      return USER_TYPES.GUEST;
    }
  }

  /**
   * 기본 이벤트 추적 (안전한 에러 처리 + 자동 user_type 포함)
   */
  const trackEvent = (eventName, parameters = {}) => {
    // 입력 검증
    if (!eventName || typeof eventName !== 'string') {
      if (isDev()) {
        console.warn('Analytics: Invalid event name:', eventName);
      }
      return false;
    }
    
    try {
      const defaultParams = {
        send_to: GA_ID,
        timestamp: Date.now(),
        page_location: window?.location?.href || '',
        page_referrer: document?.referrer || 'direct',
        user_type: getCurrentUserType() // 🔥 모든 이벤트에 자동 포함
      }
      
      const success = safeGtag('event', eventName, {
        ...defaultParams,
        ...parameters
      });
      
      // 개발 환경에서 로깅 (debug 모드에서만)
      if (isDev() && window?.location?.search?.includes('debug')) {
        console.log('📊 Analytics Event:', eventName, {
          ...defaultParams,
          ...parameters
        }, success ? '✅' : '❌');
      }
      
      return success;
    } catch (error) {
      if (isDev()) {
        console.warn('Analytics trackEvent error:', error);
      }
      return false;
    }
  }
  
  /**
   * 페이지 뷰 추적
   */
  const trackPageView = (pagePath, pageTitle = null, customParams = {}) => {
    // send_page_view:false 필수 (mimio#39) — gtag('config') 는 기본으로 page_view 를 자동 발사한다.
    // 이 호출은 page_path/page_title 을 갱신하려는 것이고, 실제 page_view 는 바로 아래에서 수동으로 보낸다.
    // 빼면 config 자동 pv + 수동 pv 로 모든 페이지가 2배로 잡힌다.
    safeGtag('config', GA_ID, {
      send_page_view: false,
      page_path: pagePath,
      page_title: pageTitle || document.title,
      custom_map: {
        user_type: 'custom_parameter_1',
        content_category: 'custom_parameter_2'
      },
      ...customParams
    })
    
    // 추가 페이지 뷰 이벤트
    trackEvent(EVENT_TYPES.PAGE_VIEW, {
      page_path: pagePath,
      page_title: pageTitle || document.title,
      ...customParams
    })
  }
  
  /**
   * 사용자 식별 설정
   */
  const setUserProperties = (userType, userId = null, customProperties = {}) => {
    const userProps = {
      user_type: userType,
      login_status: userType !== USER_TYPES.GUEST ? 'logged_in' : 'guest',
      ...customProperties
    }
    
    if (userId) {
      // send_page_view:false 필수 (mimio#39) — 라우터가 매 이동마다 부르는 경로라
      // 빼면 로그인 사용자만 page_view 가 한 건 더 붙는다.
      safeGtag('config', GA_ID, {
        send_page_view: false,
        user_id: userId,
        custom_map: userProps
      })
    }
    
    // 사용자 속성 설정
    safeGtag('set', 'user_properties', userProps)
  }
  
  /**
   * 작품 조회 추적
   */
  const trackArtworkView = (artwork) => {
    trackEvent(EVENT_TYPES.ARTWORK_VIEW, {
      content_type: CONTENT_TYPES.ARTWORK,
      content_id: artwork.id?.toString(),
      item_name: artwork.title || 'Untitled',
      artist_id: artwork.artistId?.toString(),
      artist_name: artwork.artistName,
      artwork_category: artwork.category,
      artwork_style: artwork.style,
      artwork_medium: artwork.medium,
      currency: 'KRW',
      value: artwork.price || 0
    })
  }
  
  /**
   * 아티스트 조회 추적
   */
  const trackArtistView = (artist) => {
    trackEvent(EVENT_TYPES.ARTIST_VIEW, {
      content_type: CONTENT_TYPES.ARTIST,
      content_id: artist.id?.toString(), // uuid (고유 식별용)
      item_name: artist.name || artist.displayName,
      artist_id: artist.userAccountId?.toString() || artist.id?.toString(), // userAccountId (분석용)
      artist_category: artist.category,
      artist_genre: artist.genre,
      follower_count: artist.followerCount || 0,
      artwork_count: artist.artworkCount || 0
    })
  }
  
  /**
   * 아티클 조회 추적 (뉴스/전시/스페셜)
   */
  const trackArticleView = (article, articleType = 'article') => {
    trackEvent(EVENT_TYPES.ARTICLE_VIEW, {
      content_type: CONTENT_TYPES.ARTICLE,
      content_id: article.id?.toString(),
      item_name: article.title,
      content_category: articleType, // 'news', 'exhibition', 'special'
      author: article.author,
      publish_date: article.publishDate,
      reading_time: article.readingTime
    })
  }
  
  /**
   * 좋아요/팔로우 추적
   */
  const trackLikeAction = (contentType, contentId, isLiked = true, additionalParams = {}) => {
    const eventName = contentType === CONTENT_TYPES.ARTIST ? EVENT_TYPES.ARTIST_LIKE : EVENT_TYPES.ARTWORK_LIKE
    
    trackEvent(eventName, {
      content_type: contentType,
      content_id: contentId?.toString(),
      action: isLiked ? 'like' : 'unlike',
      engagement_type: contentType === CONTENT_TYPES.ARTIST ? 'follow' : 'like',
      ...additionalParams
    })
  }
  
  /**
   * 검색 추적
   */
  const trackSearch = (searchTerm, searchCategory = 'all', resultCount = 0) => {
    trackEvent(EVENT_TYPES.SEARCH, {
      search_term: searchTerm,
      search_category: searchCategory,
      result_count: resultCount,
      content_type: CONTENT_TYPES.SEARCH
    })
  }
  
  /**
   * 공유 추적
   */
  const trackShare = (contentType, contentId, shareMethod, contentTitle = '') => {
    trackEvent(EVENT_TYPES.SHARE, {
      content_type: contentType,
      content_id: contentId?.toString(),
      item_name: contentTitle,
      method: shareMethod, // 'kakao', 'facebook', 'copy_link', etc.
      share_platform: shareMethod
    })
  }
  
  /**
   * 다운로드 추적
   */
  const trackDownload = (contentType, contentId, downloadType, fileName = '') => {
    trackEvent(EVENT_TYPES.DOWNLOAD, {
      content_type: contentType,
      content_id: contentId?.toString(),
      file_name: fileName,
      download_type: downloadType, // 'image', 'pdf', 'portfolio'
      file_extension: fileName?.split('.').pop() || 'unknown'
    })
  }
  
  /**
   * 폼 제출 추적 (로그인 시 사용자 속성 즉시 업데이트)
   */
  const trackFormSubmit = (formType, formId = '', success = true, errorMessage = '') => {
    const eventMap = {
      'inquiry': EVENT_TYPES.INQUIRY_SUBMIT,
      'contest': EVENT_TYPES.CONTEST_APPLY,
      'collaboration': EVENT_TYPES.COLLABORATION_APPLY,
      'signup': EVENT_TYPES.SIGNUP_COMPLETE,
      'login': EVENT_TYPES.LOGIN
    }
    
    // 로그인 성공 시 사용자 속성 즉시 업데이트
    if (formType === 'login' && success) {
      setTimeout(() => {
        const currentUserType = getCurrentUserType();
        setUserProperties(currentUserType);
        
        // 디버깅용 로깅
        if (isDev() && window?.location?.search?.includes('debug')) {
          console.log('🔄 Login success - Updated user type:', currentUserType);
        }
      }, 100); // localStorage 업데이트 후 실행
    }
    
    trackEvent(eventMap[formType] || 'form_submit', {
      form_type: formType,
      form_id: formId,
      success: success,
      error_message: success ? '' : errorMessage
    })
  }
  
  /**
   * 스크롤 깊이 추적 (페이지별 상세 정보 포함)
   */
  const trackScrollDepth = (percentage, additionalData = {}) => {
    // 25%, 50%, 75%, 100% 지점에서만 추적
    const milestones = [25, 50, 75, 100]
    if (milestones.includes(percentage)) {
      trackEvent(EVENT_TYPES.SCROLL_DEPTH, {
        scroll_depth: percentage,
        page_path: window.location.pathname,
        page_title: document.title,
        content_height: document.documentElement?.scrollHeight || 0,
        viewport_height: window.innerHeight || 0,
        ...additionalData
      })
    }
  }
  
  /**
   * 세션 시작 추적
   */
  const trackSessionStart = (userType = USER_TYPES.GUEST) => {
    trackEvent(EVENT_TYPES.SESSION_START, {
      user_type: userType,
      session_id: Date.now().toString(),
      device_type: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop'
    })
  }
  
  /**
   * 커스텀 전환 이벤트
   */
  const trackConversion = (conversionType, value = 0, currency = 'KRW') => {
    trackEvent('conversion', {
      conversion_type: conversionType,
      value: value,
      currency: currency
    })
  }
  
  /**
   * 오류 추적
   */
  const trackError = (errorType, errorMessage, errorStack = '') => {
    trackEvent('exception', {
      description: errorMessage,
      fatal: false,
      error_type: errorType,
      error_stack: errorStack
    })
  }
  
  /**
   * 실험 및 A/B 테스트 추적
   */
  const trackExperiment = (experimentName, variantName) => {
    safeGtag('config', GA_ID, {
      send_page_view: false, // mimio#39 — config 는 기본으로 page_view 를 자동 발사한다
      custom_map: {
        experiment_name: experimentName,
        experiment_variant: variantName
      }
    })
  }
  
  return {
    // 기본 추적 함수
    trackEvent,
    trackPageView,
    setUserProperties,
    getCurrentUserType, // 🔥 디버깅용 export 추가
    
    // 콘텐츠 특화 추적
    trackArtworkView,
    trackArtistView,
    trackArticleView,
    
    // 상호작용 추적
    trackLikeAction,
    trackSearch,
    trackShare,
    trackDownload,
    
    // 전환 추적
    trackFormSubmit,
    trackConversion,
    
    // 사용자 행동 추적
    trackScrollDepth,
    trackSessionStart,
    
    // 기타
    trackError,
    trackExperiment,
    
    // 상수 내보내기
    EVENT_TYPES,
    CONTENT_TYPES,
    USER_TYPES
  }
}